<!-- The Scorecard: the Scoreboard page's Scorecard tab, and (with team set) one team's scorecard shown in a running
     meeting (Meetings.vue). With team set it has no team tabs and leaves the page's link alone. -->
<template>
    <div>
        <div v-if="loaded && team && !activeTeam" class="sb-empty">
            <svg-icon icon="chart" />
            <div>
                You can't open the {{ teamLabel(team) }} scorecard.
                <span class="sb-muted">Only people on the team and Team Hub admins can.</span>
            </div>
        </div>

        <div v-else-if="loaded && !teams.length && !myMetrics.length" class="sb-empty">
            <svg-icon icon="chart" />
            <div>
                You don't belong to a team yet.
                <span class="sb-muted">Contact your administrator to be added to a team.</span>
            </div>
        </div>

        <template v-else-if="loaded && activeTeam">

            <!-- My numbers (every metric you own, on any team), then one tab per team (Team Hub Admins see
                 every team; everyone else just their own) -->
            <div v-if="!team" class="sb-teams" role="tablist">
                <button v-if="myEmployeeID" type="button" role="tab" :aria-selected="!searching && !!activeTeam.Mine" class="sb-team" :class="!searching && activeTeam.Mine ? 'on' : ''" @click="selectMine()">
                    <span class="sb-team-name"><svg-icon icon="user" class="sb-team-ic" />My numbers</span>
                    <span class="sb-team-meta">
                        {{ myMetrics.length }} across {{ myTeamCount }} team{{ myTeamCount == 1 ? '' : 's' }}<template v-if="mySummary.off"> · <b class="bad">{{ mySummary.off }} off</b></template><template v-if="mySummary.missing"> · <b class="warn">{{ mySummary.missing }} to enter</b></template>
                    </span>
                </button>
                <button v-for="t in teamSummaries" :key="t.Name" type="button" role="tab" :aria-selected="!searching && !activeTeam.Mine && t.Name == activeTeam.Name" class="sb-team" :class="!searching && !activeTeam.Mine && t.Name == activeTeam.Name ? 'on' : ''" @click="selectTeam(t.Name)">
                    <span class="sb-team-name">{{ teamLabel(t.Name) }}</span>
                    <span class="sb-team-meta">
                        {{ t.count }} metric{{ t.count == 1 ? '' : 's' }}<template v-if="t.off"> · <b class="bad">{{ t.off }} off</b></template><template v-if="t.missing"> · <b class="warn">{{ t.missing }} to enter</b></template>
                    </span>
                </button>
            </div>

            <div class="sb-bar">
                <div class="sb-seg">
                    <button type="button" :class="view == 'cards' ? 'on' : ''" @click="setView('cards')">Cards</button>
                    <button type="button" :class="view == 'grid' ? 'on' : ''" @click="setView('grid')">13 Weeks</button>
                </div>
                <!-- Searches every metric you can see, on every team (a tab click clears it) -->
                <div v-if="!team" class="sb-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input v-model="search" type="text" placeholder="Search all metrics" aria-label="Search all metrics" @keyup.esc="search = ''" />
                    <span v-if="searching" class="sb-search-count">{{ teamMetrics.length }} found</span>
                    <button v-if="search" type="button" class="sb-search-clear" title="Clear search" aria-label="Clear search" @click="search = ''"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                </div>
                <span style="flex:1"></span>
                <span class="sb-muted sb-tagline">One owner, one number, every week.</span>
                <!-- The Forge, set to Scorecard: on every tab, My numbers included (Keelan, 2 Nov 2026: "Scorecard has no AI") -->
                <div v-if="canAddMetrics && !team && canForge" class="ai-btn sb-btn sb-forge" @click="showForge = true"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>Improve with AI</div>
                <template v-if="!activeTeam.Mine && !searching">
                    <div v-if="$store.state.user.isAdmin" class="btn-light sb-btn" @click="showWhoCanView = true"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>Who Can View</div>
                    <div v-if="canAddMetrics" class="btn-light sb-btn" @click="openMetric(null)">Add Metric</div>
                </template>
            </div>

            <!-- The week being looked at (this week to start; the arrows step back and forward), for the selected team -->
            <div class="sb-week">
                <div class="sb-week-l">
                    <div class="sb-week-k">{{ searching ? 'Search results' : teamLabel(activeTeam.Name) }} · {{ weekAgoText }} · Wk {{ weekNumber(currentWeek) }}<a v-if="!isDueWeek" class="sb-week-today" @click.prevent="goToWeek(dueWeek)">Back to {{ dueWeek == thisWeek ? 'This Week' : 'Last Week' }}</a></div>
                    <div class="sb-week-nav">
                        <button type="button" class="sb-week-arrow" title="Previous week" aria-label="Previous week" @click="goToWeek(addDays(currentWeek, -7))">&lsaquo;</button>
                        <div class="sb-week-v">{{ weekRange(currentWeek) }}</div>
                        <button type="button" class="sb-week-arrow" title="Next week" aria-label="Next week" :disabled="currentWeek >= thisWeek" @click="goToWeek(addDays(currentWeek, 7))">&rsaquo;</button>
                    </div>
                </div>
                <div class="sb-stat"><b>{{ enteredCount }}</b>/{{ teamMetrics.length }}<span>entered</span></div>
                <div class="sb-stat" :class="offCount ? 'bad' : ''"><b>{{ offCount }}</b><span>off goal</span></div>
                <div class="sb-week-msg">
                    <template v-if="!teamMetrics.length && searching">No metrics match your search.</template>
                    <template v-else-if="!teamMetrics.length && activeTeam.Mine">You don't own any metrics yet.</template>
                    <template v-else-if="!teamMetrics.length">No metrics yet. Most teams track 5 to 15 weekly numbers.</template>
                    <template v-else-if="activeTeam.Mine || searching || !isDueWeek">{{ missingMetrics.length ? missingMetrics.length + (isDueWeek ? ' still to enter.' : ' not entered for this week.') : 'All numbers are in.' }}</template>
                    <template v-else-if="missingMetrics.length">
                        {{ missingMetrics.length }} still to enter, due {{ dueText(activeTeam.DueDay, activeTeam.DueTime) }}.
                        <template v-if="waitingOn.length">Waiting on {{ waitingOn.join(', ') }}.</template>
                    </template>
                    <template v-else>All numbers are in. Due {{ dueText(activeTeam.DueDay, activeTeam.DueTime) }}.</template>
                </div>
                <div v-if="isDueWeek && waitingOnOthers.length && !searching" class="btn-light sb-btn" :class="nudged[activeTeam.Name] ? 'disable-button' : ''" @click="showNudge = true">{{ nudged[activeTeam.Name] ? 'Reminded' : 'Nudge ' + waitingOnOthers.length }}</div>
                <div v-if="teamMetrics.length" class="btn sb-btn" @click="entryMetric = null; showEntry = true">Enter Numbers</div>
                <button v-if="!activeTeam.Mine && !searching" type="button" class="sb-due-btn" title="When this team's numbers are due" @click="openDue()"><svg-icon icon="gear" /></button>
            </div>

            <div v-if="!teamMetrics.length" class="sb-empty sb-empty-team">
                <svg-icon icon="chart" />
                <template v-if="searching">
                    <div>No metrics match "{{ search.trim() }}".</div>
                    <div class="btn-light sb-btn" @click="search = ''">Clear Search</div>
                </template>
                <template v-else-if="activeTeam.Mine">
                    <div>You don't own any metrics yet. Metrics you're the owner of, on any team, show up here.</div>
                </template>
                <template v-else>
                    <div>{{ teamLabel(activeTeam.Name) }} doesn't have any metrics yet.</div>
                    <div v-if="canAddMetrics" class="btn sb-btn" @click="openMetric(null)">Add the First Metric</div>
                </template>
            </div>

            <!-- Cards -->
            <div v-else-if="view == 'cards'" class="sb-cards">
                <template v-for="g in metricGroups" :key="g.team || 'team'">
                <div v-if="g.team" class="sb-group-h">{{ teamLabel(g.team) }}<span>{{ g.metrics.length }}</span></div>
                <div v-for="m in g.metrics" :key="m.ID" class="sb-card" :class="cardState(m).cls">
                    <div class="sb-card-top">
                        <div class="sb-card-ic"><svg-icon :icon="metricIcon(m)" /></div>
                        <div class="sb-card-nm">
                            <button type="button" class="sb-mname sb-card-title" :class="canAddMetrics && m.CanEdit ? '' : 'sb-mname-static'" @click="openMetric(m)">{{ m.Name }}</button>
                            <div class="sb-card-own">{{ activeTeam.Mine && !searching ? 'Due ' + dueText(m.DueDay, m.DueTime) : ownerName(m) }}</div>
                        </div>
                        <span class="sb-pill" :class="cardState(m).cls">{{ cardState(m).label }}</span>
                    </div>
                    <div v-if="offStreak(m, weeks) >= 3" class="sb-streak"><svg-icon icon="alerttriangle" />Off goal {{ offStreak(m, weeks) }} weeks in a row</div>
                    <div class="sb-stats">
                        <div><span>{{ relativeWeekLabel(currentWeek) }}</span><b :class="statClass(m)">{{ hasValue(m, currentWeek) ? formatShort(m, m.Values[currentWeek]) : '—' }}</b></div>
                        <div><span>Goal</span><b>{{ goalText(m, currentWeek, true) }}</b></div>
                        <div><span>4-wk avg</span><b>{{ fourWeekAvg(m) === null ? '—' : formatShort(m, fourWeekAvg(m)) }}</b></div>
                    </div>
                    <div v-if="m.Definition" class="sb-def">{{ m.Definition }}</div>
                    <button v-else-if="canAddMetrics && m.CanEdit" type="button" class="sb-def sb-def-none" @click="openMetric(m)">No definition. Add how it's counted.</button>
                    <div class="sb-prog-wrap">
                        <div class="sb-prog" :class="passes(m, m.Values[currentWeek], currentWeek) ? 'ok' : ''"><span :style="'width:' + progress(m, m.Values[currentWeek], currentWeek) + '%'"></span></div>
                        <span v-if="trend(m)" class="sb-trend" :class="trend(m).cls">{{ trend(m).text }}</span>
                    </div>
                    <div class="sb-card-acts">
                        <button type="button" class="sb-card-b sb-card-b-main" @click="entryMetric = m; showEntry = true">{{ hasValue(m, currentWeek) ? 'Update' : 'Enter Week' }}</button>
                        <button type="button" class="sb-card-b" @click="trendMetric = m">Trend</button>
                        <button type="button" class="sb-card-b sb-card-b-issue" @click="openIssue(m)">Issue</button>
                    </div>
                </div>
                </template>
                <button v-if="!activeTeam.Mine && !searching && canAddMetrics" type="button" class="sb-card-add" @click="openMetric(null)"><svg-icon icon="plus" />Add Metric to {{ teamLabel(activeTeam.Name) }}</button>
            </div>

            <!-- 13 weeks, each week colored against the goal it had -->
            <template v-else>
                <div class="sb-grid-wrap">
                    <table class="sb-grid">
                        <thead>
                            <tr>
                                <th class="sb-o">Who</th>
                                <th class="sb-m">Metric</th>
                                <th class="sb-g">Goal</th>
                                <th v-for="(w, i) in weeks" :key="w" :class="i == weeks.length - 1 ? 'now' : ''" :title="'Week of ' + weekRange(w)">{{ weekLabel(w) }}</th>
                                <th class="sb-avg">13-wk avg</th>
                            </tr>
                        </thead>
                        <tbody v-for="g in metricGroups" :key="g.team || 'team'">
                            <!-- My numbers: a header row per team (the name stays put when the weeks scroll sideways) -->
                            <tr v-if="g.team" class="sb-group-row">
                                <td :colspan="weeks.length + 4"><span class="sb-group-h">{{ teamLabel(g.team) }}<span>{{ g.metrics.length }}</span></span></td>
                            </tr>
                            <tr v-for="m in g.metrics" :key="m.ID">
                                <td class="sb-o"><span class="sb-own" :title="ownerName(m)">{{ initials(ownerName(m)) }}</span></td>
                                <td class="sb-m">
                                    <button type="button" class="sb-mname" :class="canAddMetrics && m.CanEdit ? '' : 'sb-mname-static'" :title="m.Definition" @click="openMetric(m)">{{ m.Name }}</button>
                                    <span v-if="offStreak(m, weeks) >= 3" class="sb-sk" :title="'Off goal ' + offStreak(m, weeks) + ' weeks in a row'"><svg-icon icon="alerttriangle" />{{ offStreak(m, weeks) }}</span>
                                </td>
                                <td class="sb-g"><span class="sb-goal">{{ goalText(m, currentWeek, true) }}</span></td>
                                <template v-for="(w, i) in weeks" :key="w">
                                    <td v-if="editCell && editCell.metricID == m.ID && editCell.week == w" class="sb-c editing">
                                        <input type="number" step="any" class="sb-cellin" v-model="editCell.value" ref="cellInput" :aria-label="m.Name + ', week of ' + weekLabel(w)" @keyup.enter="saveCell()" @keyup.esc="editCell = null" @blur="saveCell()" />
                                    </td>
                                    <td v-else class="sb-c" :class="cellClass(m, w, i)" :title="'Week of ' + weekRange(w) + ' · goal ' + goalText(m, w) + (goalChangedAt(m, w) ? ' (goal changed this week)' : '')" @click="startCell(m, w)">
                                        <template v-if="hasValue(m, w)">{{ formatShort(m, m.Values[w]) }}</template>
                                        <span v-else-if="i == weeks.length - 1" class="sb-enter">enter</span>
                                        <template v-else>—</template>
                                    </td>
                                </template>
                                <td class="sb-avg">{{ average(m, weeks) === null ? '—' : formatShort(m, average(m, weeks)) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="sb-legend">
                    <span><i class="pass"></i>On goal</span>
                    <span><i class="fail"></i>Off goal</span>
                    <span><i></i>Not entered</span>
                    <span><i class="gchg"></i>Goal changed</span>
                    <span class="sb-muted">Each week is colored against the goal that was set that week.</span>
                </div>
            </template>
        </template>

        <scoreboard-metric-modal v-if="showMetric" :metric="metricToEdit" :team="activeTeam.Name" :teams="teams.map(t => t.Name)" :employees="employees" :week="thisWeek" @close="showMetric = false" @saved="onMetricSaved" />
        <scoreboard-entry-modal v-if="showEntry" :metrics="teamMetrics" :team="searching ? '' : activeTeam.Name" :metric="entryMetric" :week="currentWeek" :employees="employees" :myEmployeeID="myEmployeeID" @close="closeEntry()" @saved="onEntrySaved" />
        <scoreboard-nudge-modal v-if="showNudge" :team="activeTeam.Name" :week="currentWeek" @close="showNudge = false" @sent="onNudgeSent" />
        <forge-team v-if="showForge" modal :only="[ 'improvemetrics', 'scorecard' ]" :start-kind="teamMetrics.length ? 'improvemetrics' : 'scorecard'" :preset-team="activeTeam && !activeTeam.Mine ? activeTeam.Name : ''" @applied="load()" @close="showForge = false" />
        <scoreboard-who-can-view v-if="showWhoCanView" :team="activeTeam.Name" @close="showWhoCanView = false" />
        <scoreboard-trend-modal v-if="trendMetric" :metric="trendMetric" :weeks="weeks" :owner-name="ownerName(trendMetric)" @close="trendMetric = null" @enter="enterFromTrend()" />
        <!-- A metric's Issue: a card on a meeting board (the running meeting's, when in one) -->
        <quick-add-agenda-item v-if="issue" heading="Add Issue" prefer-column="Issues" :preset="issue.preset" :team="issue.team" :fixed-meeting="meeting" @close="issue = null" />

        <div v-if="dueForm" class="error-msg-wrap">
            <div class="form-wrapper slide-up" style="width:420px">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="dueForm = null">&times;</button>
                </div>
                <h2 class="sb-modal-title">When {{ teamLabel(activeTeam.Name) }} numbers are due</h2>
                <div class="sb-due-row">
                    <div>
                        <div class="sb-label">Day</div>
                        <select v-model.number="dueForm.DueDay">
                            <option v-for="(d, i) in DAY_NAMES" :key="i" :value="i">{{ d }}</option>
                        </select>
                    </div>
                    <div>
                        <div class="sb-label">Time</div>
                        <input type="time" v-model="dueForm.DueTime" />
                    </div>
                </div>
                <!-- Which week is asked for first: this page, Enter Numbers, Employee Home and nudges all start on it -->
                <div class="sb-label" style="margin-top:14px">Numbers are for</div>
                <select v-model="dueForm.ReportWeek">
                    <option value="last">The week just finished</option>
                    <option value="current">The current week</option>
                </select>
                <p class="sb-intro" style="margin-top:6px">Due {{ dueText(dueForm.DueDay, dueForm.DueTime) }} for {{ dueForm.ReportWeek == 'current' ? 'that same week' : 'the week before' }}.</p>
                <div class="sb-modal-acts">
                    <div class="btn-light" @click="dueForm = null">Cancel</div>
                    <div class="btn" @click="saveDue()">Save</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import ScoreboardMetricModal from '@/components/ScoreboardMetricModal.vue';
import ForgeTeam from '@/components/ForgeTeam.vue';
import ScoreboardEntryModal from '@/components/ScoreboardEntryModal.vue';
import ScoreboardTrendModal from '@/components/ScoreboardTrendModal.vue';
import ScoreboardNudgeModal from '@/components/ScoreboardNudgeModal.vue';
import ScoreboardWhoCanView from '@/components/ScoreboardWhoCanView.vue';
import QuickAddAgendaItem from '@/components/QuickAddAgendaItem.vue';
import {
    DAY_NAMES, mondayOf, addDays, recentWeeks, weekLabel, weekRange, goalAt, passes, goalText, formatValue, formatShort,
    average, offStreak, progress, initials, dueText, teamLabel, COMPANY_TEAM, dueWeek as teamDueWeek, dueWeekOf, relativeWeekLabel, weekNumber,
    metricIssue
} from '@/scoreboard';
import { refreshNumbersToEnter } from '@/myNumbers';

export default defineComponent({
    name: 'ScorecardView',
    props: {
        // Show just this team (no tabs, no ?team / ?mine in the link)
        team: { type: String, default: '' },
        // The meeting being run (Meetings.vue): Issues go on its board
        meeting: { type: Object, default: null }
    },
    components: {
        SvgIcon,
        QuickAddAgendaItem,
        ForgeTeam,
        ScoreboardMetricModal,
        ScoreboardEntryModal,
        ScoreboardTrendModal,
        ScoreboardNudgeModal,
        ScoreboardWhoCanView
    },
    data() {
        const currentWeek = mondayOf(new Date());
        return {
            DAY_NAMES,
            thisWeek: currentWeek, // the real current week; currentWeek is the one being looked at (dueWeek to start)
            currentWeek,
            weeks: recentWeeks(currentWeek),
            loaded: false,
            teams: [] as any[],
            metrics: [] as any[],
            employees: [] as any[],
            myEmployeeID: null as any,
            selectedTeam: '',
            showMine: false, // the My numbers tab (?mine=1)
            search: '',
            view: 'cards',
            showMetric: false,
            metricToEdit: null as any,
            showEntry: false,
            entryMetric: null as any,
            trendMetric: null as any,
            // The metric whose trend chart opened the numbers form, so closing the form goes back to it
            returnToTrendID: null as any,
            editCell: null as any,
            showNudge: false,
            showWhoCanView: false,
            showForge: false,
            issue: null as any, // { preset, team }: the Add Issue form
            // Teams reminded since the page loaded, so the button isn't pressed twice by accident
            nudged: {} as any,
            dueForm: null as any
        }
    },
    async created() {
        try {
            this.view = localStorage.getItem('scoreboardView') == 'grid' ? 'grid' : 'cards';
        } catch(e) { /* storage unavailable; cards it is */ }
        if(this.team) {
            await this.load();
            await this.showDueWeek();
            return;
        }
        this.selectedTeam = String(this.$route.query.team || '');
        this.showMine = this.$route.query.mine == '1';
        await this.load();
        // With nothing picked in the link, start on My numbers when you own any
        if(!this.$route.query.team && !this.$route.query.mine && this.myMetrics.length) {
            this.showMine = true;
        }
        await this.showDueWeek();
    },
    computed: {
        // The week this team's numbers are being asked for (its due settings, scoreboard.ts dueWeek): where the page
        // opens, and the week "still to enter", nudges and "due" are about
        dueWeek(): string {
            if(!this.activeTeam) {
                return this.thisWeek;
            }
            return this.activeTeam.Mine ? dueWeekOf(this.myMetrics) : teamDueWeek(this.activeTeam.ReportWeek);
        },
        isDueWeek(): boolean {
            return this.currentWeek == this.dueWeek;
        },
        // "this week", "last week", "3 weeks ago"
        weekAgoText(): string {
            const weeks = Math.round((new Date(this.thisWeek + 'T00:00:00').getTime() - new Date(this.currentWeek + 'T00:00:00').getTime()) / (7 * 86400000));
            return weeks <= 0 ? 'this week' : weeks == 1 ? 'last week' : `${weeks} weeks ago`;
        },
        // Only Team Hub Admins add and change metrics (scoreboard.ts POST /metric); numbers are open to the team
        // The Forge's Scorecard tools are for Admin and Editor logins (routes/ai.ts)
        canForge(): boolean {
            const u = this.$store.state.user;
            return !!u.isAdmin && [ 'Admin', 'Editor' ].includes(u.role);
        },
        canAddMetrics(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin';
        },
        // The fixed team (null when you can't open it); else My numbers (a stand-in team, Mine: true), else the
        // selected team, else your own team, else the first one
        activeTeam(): any {
            if(this.team) {
                return this.teams.find((t: any) => t.Name == this.team) || null;
            }
            if(this.showMine && this.myEmployeeID) {
                return { Name: 'My numbers', Mine: true };
            }
            const myTeam = this.employees.find((e: any) => e.ID == this.myEmployeeID)?.Team;
            return this.teams.find((t: any) => t.Name == this.selectedTeam)
                || this.teams.find((t: any) => t.Name == myTeam)
                || this.teams[0]
                || (this.myEmployeeID ? { Name: 'My numbers', Mine: true } : null);
        },
        // A search: just on the page (not in a meeting), and over every team's metrics
        searching(): boolean {
            return !this.team && !!this.search.trim();
        },
        // The tab's metrics, or what a search matches (every word, in the name, definition, owner or team)
        teamMetrics(): any[] {
            if(!this.activeTeam) {
                return [];
            }
            if(this.searching) {
                const words = this.search.trim().toLowerCase().split(/\s+/);
                return this.metrics.filter((m: any) => {
                    const text = [ m.Name, m.Definition, this.ownerName(m), teamLabel(m.Team) ].join(' ').toLowerCase();
                    return words.every((w: string) => text.includes(w));
                });
            }
            return this.activeTeam.Mine ? this.myMetrics : this.metrics.filter((m: any) => m.Team == this.activeTeam.Name);
        },
        // Every metric you own, on any team (the API includes ones on teams you can't otherwise open)
        myMetrics(): any[] {
            return this.myEmployeeID ? this.metrics.filter((m: any) => m.OwnerEmployeeID == this.myEmployeeID) : [];
        },
        // What the cards and the 13-week grid show: one group with no header on a team tab; on My numbers and search
        // results, one per team with a header, in the tab order (Company first), then any teams you can't open (where you
        // just own metrics)
        metricGroups(): { team: string | null, metrics: any[] }[] {
            if(!this.activeTeam?.Mine && !this.searching) {
                return [ { team: null, metrics: this.teamMetrics } ];
            }
            const order = this.teams.map((t: any) => t.Name);
            const names = [ ...new Set(this.teamMetrics.map((m: any) => m.Team)) ] as string[];
            names.sort((a, b) => {
                const ia = order.indexOf(a), ib = order.indexOf(b);
                return ia >= 0 && ib >= 0 ? ia - ib : ia >= 0 ? -1 : ib >= 0 ? 1 : teamLabel(a).localeCompare(teamLabel(b));
            });
            return names.map(team => ({ team, metrics: this.teamMetrics.filter((m: any) => m.Team == team) }));
        },
        myTeamCount(): number {
            return new Set(this.myMetrics.map((m: any) => m.Team)).size;
        },
        mySummary(): { missing: number, off: number } {
            return {
                missing: this.myMetrics.filter((m: any) => !this.hasValue(m, this.currentWeek)).length,
                off: this.myMetrics.filter((m: any) => passes(m, m.Values[this.currentWeek], this.currentWeek) === false).length
            };
        },
        teamSummaries(): any[] {
            return this.teams.map((t: any) => {
                const ms = this.metrics.filter((m: any) => m.Team == t.Name);
                return {
                    Name: t.Name,
                    count: ms.length,
                    missing: ms.filter((m: any) => !this.hasValue(m, this.currentWeek)).length,
                    off: ms.filter((m: any) => passes(m, m.Values[this.currentWeek], this.currentWeek) === false).length
                };
            });
        },
        enteredCount(): number {
            return this.teamMetrics.filter((m: any) => this.hasValue(m, this.currentWeek)).length;
        },
        offCount(): number {
            return this.teamMetrics.filter((m: any) => passes(m, m.Values[this.currentWeek], this.currentWeek) === false).length;
        },
        missingMetrics(): any[] {
            return this.teamMetrics.filter((m: any) => !this.hasValue(m, this.currentWeek));
        },
        // First names of the owners with a number still to enter
        waitingOn(): string[] {
            const names = this.missingMetrics.map((m: any) => this.employees.find((e: any) => e.ID == m.OwnerEmployeeID)?.Name).filter((n: any) => n);
            return [ ...new Set(names.map((n: string) => n.trim().split(/\s+/)[0])) ] as string[];
        },
        // Owners other than you with a number still to enter (who a nudge would go to)
        waitingOnOthers(): number[] {
            return [ ...new Set(this.missingMetrics.map((m: any) => m.OwnerEmployeeID).filter((id: any) => id && id != this.myEmployeeID)) ] as number[];
        }
    },
    methods: {
        weekLabel,
        weekRange,
        relativeWeekLabel,
        weekNumber,
        goalText,
        formatValue,
        formatShort,
        passes,
        average,
        offStreak,
        progress,
        initials,
        dueText,
        addDays,
        // Goes to the selected team's due week
        async showDueWeek() {
            if(!this.isDueWeek) {
                await this.goToWeek(this.dueWeek);
            }
        },
        // Looks at another week: the cards, counts and 13-week grid all end with it. Not past this week.
        async goToWeek(week: string) {
            this.currentWeek = week > this.thisWeek ? this.thisWeek : week;
            this.weeks = recentWeeks(this.currentWeek);
            this.editCell = null;
            await this.load();
        },
        async load() {
            await axios.get(`${this.$store.state.apiUrl}/api/scoreboard`, { params: { week: this.currentWeek } })
                .then(resp => {
                    // Company 1 doesn't use the Company scorecard (Rocks still shows it)
                    const hideCompany = this.$store.state.user.companyID == 1;
                    this.teams = resp.data.Teams.filter((t: any) => !hideCompany || t.Name != COMPANY_TEAM);
                    this.metrics = resp.data.Metrics.filter((m: any) => !hideCompany || m.Team != COMPANY_TEAM);
                    this.employees = resp.data.Employees.map((e: any) => ({ ...e, ID: Number(e.ID) }));
                    this.myEmployeeID = resp.data.MyEmployeeID;
                })
                .catch(e => {
                    console.log(e);
                })
            this.loaded = true;
        },
        selectTeam(name: string) {
            if(this.team) {
                return;
            }
            this.selectedTeam = name;
            this.showMine = false;
            this.search = '';
            this.editCell = null;
            this.$router.replace({ query: { ...this.$route.query, team: name, mine: undefined } });
            this.showDueWeek();
        },
        selectMine() {
            this.showMine = true;
            this.search = '';
            this.editCell = null;
            this.$router.replace({ query: { ...this.$route.query, team: undefined, mine: '1' } });
            this.showDueWeek();
        },
        setView(view: string) {
            this.view = view;
            this.editCell = null;
            try {
                localStorage.setItem('scoreboardView', view);
            } catch(e) { /* not remembered, that's fine */ }
        },
        hasValue(m: any, week: string): boolean {
            return m.Values[week] !== undefined && m.Values[week] !== null;
        },
        ownerName(m: any): string {
            return this.employees.find((e: any) => e.ID == m.OwnerEmployeeID)?.Name || 'No owner';
        },
        metricIcon(m: any): string {
            if(m.Unit == '$' || m.Unit == '$K') return 'dollar';
            if(/time|min|hour|day/i.test(m.Name + ' ' + m.Unit)) return 'clock';
            return 'chart';
        },
        cardState(m: any): { label: string, cls: string } {
            const p = passes(m, m.Values[this.currentWeek], this.currentWeek);
            if(p === null) return { label: 'Not entered', cls: 'warn' };
            return p ? { label: 'On goal', cls: 'ok' } : { label: 'Off goal', cls: 'bad' };
        },
        statClass(m: any): string {
            const p = passes(m, m.Values[this.currentWeek], this.currentWeek);
            return p === null ? '' : p ? 'ok' : 'bad';
        },
        fourWeekAvg(m: any): number | null {
            return average(m, this.weeks.slice(-4));
        },
        // Change vs last week, green when it's the right direction for the goal
        trend(m: any): any {
            const now = m.Values[this.currentWeek];
            const prev = m.Values[addDays(this.currentWeek, -7)];
            if(now === undefined || now === null) {
                return { text: this.isDueWeek ? `Not entered, due ${dueText(m.DueDay, m.DueTime)}` : 'Not entered', cls: 'warn' };
            }
            if(prev === undefined || prev === null || prev == 0) {
                return null;
            }
            const change = Math.round((now - prev) / Math.abs(prev) * 100);
            const g = goalAt(m, this.currentWeek);
            const good = g && g.Cmp == 'lte' ? change <= 0 : change >= 0;
            return { text: `${change > 0 ? '+' : ''}${change}% vs ${this.currentWeek == this.thisWeek ? 'last week' : 'week before'}`, cls: change == 0 ? '' : good ? 'ok' : 'bad' };
        },
        goalChangedAt(m: any, week: string): boolean {
            return (m.Goals || []).some((g: any, i: number) => i > 0 && g.FromWeek == week);
        },
        cellClass(m: any, week: string, i: number): string {
            const p = passes(m, m.Values[week], week);
            return [
                p === null ? 'none' : p ? 'pass' : 'fail',
                i == this.weeks.length - 1 ? 'now' : '',
                this.goalChangedAt(m, week) ? 'gchg' : ''
            ].join(' ');
        },
        startCell(m: any, week: string) {
            this.editCell = { metricID: m.ID, week, value: this.hasValue(m, week) ? m.Values[week] : '' };
            this.$nextTick(() => {
                const ref = this.$refs.cellInput as any;
                (Array.isArray(ref) ? ref[0] : ref)?.focus();
            });
        },
        async saveCell() {
            const cell = this.editCell;
            if(!cell) {
                return;
            }
            this.editCell = null;
            const m = this.metrics.find((x: any) => x.ID == cell.metricID);
            const before = m && this.hasValue(m, cell.week) ? String(m.Values[cell.week]) : '';
            if(!m || before === String(cell.value)) {
                return;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/values`, { Values: [ { MetricID: m.ID, Week: cell.week, Value: cell.value } ] })
                .catch(e => {
                    console.log(e);
                    alert('There was an error saving that number');
                })
            await this.load();
            refreshNumbersToEnter();
        },
        // Adding and editing metrics is for Team Hub Admins (CanEdit, from the API, also needs the metric's team);
        // everyone else just enters numbers
        openMetric(m: any) {
            if(!this.canAddMetrics || (m && !m.CanEdit)) {
                return;
            }
            this.metricToEdit = m;
            this.showMetric = true;
        },
        async onMetricSaved(team: string) {
            this.showMetric = false;
            // Stays on the search results
            if(team && !this.activeTeam.Mine && !this.searching) {
                this.selectTeam(team);
            }
            await this.load();
        },
        // Started with the metric's number for the week being looked at, at High priority
        openIssue(m: any) {
            this.issue = { preset: { ...metricIssue(m, this.currentWeek, this.ownerName(m)), Priority: 'high' }, team: m.Team };
        },
        enterFromTrend() {
            this.entryMetric = this.trendMetric;
            this.returnToTrendID = this.trendMetric.ID;
            this.trendMetric = null;
            this.showEntry = true;
        },
        closeEntry() {
            this.showEntry = false;
            this.reopenTrend();
        },
        async onEntrySaved() {
            this.showEntry = false;
            await this.load();
            // The header's Numbers button
            refreshNumbersToEnter();
            this.reopenTrend();
        },
        // Back to the chart the form was opened from, with the freshly loaded numbers
        reopenTrend() {
            if(this.returnToTrendID) {
                this.trendMetric = this.metrics.find((m: any) => m.ID == this.returnToTrendID) || null;
                this.returnToTrendID = null;
            }
        },
        onNudgeSent(result: any) {
            this.showNudge = false;
            this.nudged[this.activeTeam.Name] = true;
            const list = (names: string[]) => names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : names[0];
            const parts = [];
            if(result.Sent.length) parts.push(`Texted ${list(result.Sent)}.`);
            if(result.Failed.length) parts.push(`Couldn't text ${list(result.Failed)}.`);
            if(result.NoPhone.length) parts.push(`${list(result.NoPhone)} ${result.NoPhone.length == 1 ? 'has' : 'have'} no phone number.`);
            // The corner message the header's quick adds use (App.vue), so the page doesn't shift
            this.$store.state.quickAddToast = { text: parts.join(' ') || 'No one to remind.' };
        },
        openDue() {
            this.dueForm = { DueDay: this.activeTeam.DueDay, DueTime: this.activeTeam.DueTime, ReportWeek: this.activeTeam.ReportWeek || 'last' };
        },
        async saveDue() {
            await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/due`, { Team: this.activeTeam.Name, ...this.dueForm })
                .then(() => {
                    this.dueForm = null;
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
            await this.load();
            await this.showDueWeek();
        }
    }
});
</script>

<style scoped>
.sb-muted { color: var(--font-mid-gray); font-size: 12.5px; font-weight: normal; }
.sb-btn { display: inline-flex; align-items: center; gap: 6px; margin: 0; padding: 7px 14px; white-space: nowrap; }
.sb-btn.sb-forge svg { width: 15px; height: 15px; fill: currentColor; stroke: none; }
/* Same icon style as the page-bar buttons (.pb-btn svg) */
.sb-btn svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }

.sb-empty { display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; padding: 24px; color: var(--font-dark-gray); max-width: 700px; }
.sb-empty svg { width: 28px; height: 28px; fill: var(--font-mid-gray); flex-shrink: 0; }
.sb-empty .sb-muted { display: block; margin-top: 3px; }
.sb-empty-team { flex-direction: column; text-align: center; max-width: none; padding: 40px 24px; }

/* Team tabs: shared with the Rocks tab, in style.scss (.sb-teams) */

.sb-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.sb-seg { display: inline-flex; background: #fff; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 2px; }
.sb-seg button { background: none; border: none; font-family: inherit; font-size: 12.5px; color: var(--font-dark-gray); padding: 6px 13px; border-radius: 6px; cursor: pointer; }
.sb-seg button.on { background: var(--primary-color); color: #fff; font-weight: 600; }

/* This-week banner */
.sb-week { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; background: #fff; border: 1px solid var(--mid-gray); border-left: 4px solid var(--primary-color); border-radius: 12px; padding: 14px 18px; margin-bottom: 14px; }
.sb-week-k { font-size: 11.5px; font-weight: 700; color: var(--font-mid-gray); }
.sb-week-v { font-family: var(--head-font); font-weight: 700; font-size: 17px; }
.sb-week-nav { display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.sb-week-arrow { width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; padding: 0 0 2px; border: 1px solid var(--mid-gray); border-radius: 6px; background: #fff; color: var(--dark-gray); font-size: 18px; line-height: 1; cursor: pointer; }
.sb-week-arrow:hover:not(:disabled) { border-color: var(--primary-color); color: var(--primary-color); }
.sb-week-arrow:disabled { opacity: .35; cursor: default; }
.sb-week-today { margin-left: 8px; font-weight: 600; text-decoration: underline; cursor: pointer; }
.sb-stat { display: flex; flex-direction: column; align-items: center; min-width: 56px; color: var(--font-dark-gray); }
.sb-stat b { font-family: var(--head-font); font-size: 22px; line-height: 1.1; }
.sb-stat span { font-size: 11.5px; color: var(--font-mid-gray); }
.sb-stat.bad b { color: var(--primary-color); }
.sb-week-msg { flex: 1; min-width: 220px; font-size: 13px; color: var(--font-dark-gray); line-height: 1.45; }
.sb-due-btn { background: none; border: none; padding: 4px; cursor: pointer; display: flex; }
.sb-due-btn svg { width: 18px; height: 18px; fill: var(--font-mid-gray); }
.sb-due-btn:hover svg { fill: var(--dark-gray); }

/* Metric cards */
.sb-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
/* My numbers: a team's header, across the whole card grid / table row */
.sb-group-h { display: flex; align-items: center; gap: 8px; font-family: var(--head-font); font-weight: 700; font-size: 15px; color: var(--dark-gray); }
.sb-group-h span { font-family: var(--body-font); font-size: 11px; font-weight: 700; color: var(--font-dark-gray); background: var(--mid-gray); border-radius: 9px; padding: 1px 8px; }
.sb-cards .sb-group-h { grid-column: 1 / -1; margin-top: 8px; }
.sb-cards .sb-group-h:first-child { margin-top: 0; }
.sb-grid .sb-group-row td { text-align: left; background: var(--light-gray); padding: 8px 10px; }
.sb-grid .sb-group-row .sb-group-h { display: inline-flex; position: sticky; left: 10px; font-size: 13px; }
.sb-card { background: #fff; border: 1px solid var(--mid-gray); border-radius: 16px; padding: 18px; display: flex; flex-direction: column; gap: 14px; box-shadow: 0 1px 2px rgba(44,41,38,.04); }
.sb-card-top { display: flex; align-items: flex-start; gap: 12px; }
.sb-card-ic { width: 38px; height: 38px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: var(--light-gray); }
.sb-card-ic svg { width: 18px; height: 18px; fill: currentColor; }
.sb-card.ok .sb-card-ic { background: #E3F4E8; color: #2E7D46; }
.sb-card.bad .sb-card-ic { background: #FCEBEC; color: var(--primary-color); }
.sb-card.warn .sb-card-ic { background: #FBF0DA; color: #9A6B15; }
.sb-card-nm { flex: 1; min-width: 0; }
.sb-card-title { font-family: var(--head-font); font-weight: 700; font-size: 15.5px; line-height: 1.3; }
.sb-card-own { font-size: 12.5px; color: var(--font-mid-gray); margin-top: 2px; }
.sb-pill { font-size: 11.5px; font-weight: 700; border-radius: 12px; padding: 3px 10px; white-space: nowrap; }
.sb-pill.ok { background: #E3F4E8; color: #2E7D46; }
.sb-pill.bad { background: #FCEBEC; color: var(--primary-color); }
.sb-pill.warn { background: #FBF0DA; color: #9A6B15; }
.sb-streak { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: var(--primary-color); background: #FCEBEC; border-radius: 8px; padding: 5px 10px; margin-top: -4px; }
.sb-streak svg { width: 13px; height: 13px; fill: currentColor; }
.sb-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.sb-stats > div { background: var(--light-gray); border-radius: 10px; padding: 10px 12px; min-width: 0; }
.sb-stats span { display: block; font-size: 11px; font-weight: 600; color: var(--font-mid-gray); margin-bottom: 3px; }
.sb-stats b { display: block; font-family: var(--head-font); font-size: 18px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sb-stats b.bad { color: var(--primary-color); }
.sb-stats b.ok { color: #2E7D46; }
.sb-def { font-size: 12px; color: var(--font-dark-gray); line-height: 1.45; margin-top: -4px; background: none; border: none; padding: 0; text-align: left; font-family: inherit; }
.sb-def-none { color: #9A6B15; cursor: pointer; text-decoration: underline; }
.sb-prog-wrap { display: flex; flex-direction: column; gap: 7px; }
.sb-prog { height: 7px; background: var(--mid-gray); border-radius: 4px; overflow: hidden; }
.sb-prog > span { display: block; height: 100%; background: var(--primary-color); border-radius: 4px; }
.sb-prog.ok > span { background: #2E7D46; }
.sb-trend { font-size: 12px; font-weight: 600; color: var(--font-mid-gray); }
.sb-trend.ok { color: #2E7D46; }
.sb-trend.bad { color: var(--primary-color); }
.sb-trend.warn { color: #9A6B15; }
.sb-card-acts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.sb-card-b { color: inherit; border: 1px solid var(--mid-gray); background: var(--light-gray); border-radius: 10px; padding: 9px 6px; font-size: 12.5px; font-weight: 600; font-family: inherit; cursor: pointer; }
.sb-card-b:hover { border-color: var(--font-mid-gray); }
.sb-card-b.sb-card-b-main { background: var(--dark-gray); border-color: var(--dark-gray); color: #fff; }
/* The Not entered yellow */
.sb-card-b.sb-card-b-issue { background: #FBF0DA; border-color: #F0DDB4; color: #9A6B15; }
.sb-card-b.sb-card-b-issue:hover { border-color: #9A6B15; }
.sb-card-add { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; min-height: 200px; border: 1.5px dashed var(--mid-gray); border-radius: 16px; background: none; color: var(--font-mid-gray); font-weight: 600; font-size: 13px; font-family: inherit; cursor: pointer; }
.sb-card-add svg { width: 20px; height: 20px; fill: currentColor; }
.sb-card-add:hover { border-color: var(--primary-color); color: var(--primary-color); }
.sb-mname { background: none; border: none; padding: 0; font: inherit; color: inherit; text-align: left; cursor: pointer; }
.sb-mname:hover { color: var(--primary-color); text-decoration: underline; }
/* Not editable (only Team Hub Admins edit metrics): just the name */
.sb-mname-static { cursor: default; }
.sb-mname-static:hover { color: inherit; text-decoration: none; }

/* 13-week grid */
.sb-grid-wrap { background: #fff; border: 1px solid var(--mid-gray); border-radius: 14px; overflow: auto; }
table.sb-grid { border-collapse: separate; border-spacing: 0; font-size: 12.5px; min-width: 900px; width: 100%; }
.sb-grid th, .sb-grid td { padding: 9px 8px; text-align: center; white-space: nowrap; border-bottom: 1px solid var(--light-gray); }
.sb-grid thead th { background: var(--light-gray); font-weight: 700; font-size: 11px; color: var(--font-dark-gray); position: sticky; top: 0; z-index: 3; }
.sb-grid thead th.now { background: var(--mid-gray); }
.sb-o, .sb-m, .sb-g { text-align: left !important; position: sticky; background: #fff; z-index: 2; }
.sb-o { left: 0; width: 46px; }
.sb-m { left: 46px; min-width: 200px; font-weight: 600; }
.sb-g { left: 246px; width: 80px; border-right: 2px solid var(--mid-gray); }
.sb-grid thead .sb-o, .sb-grid thead .sb-m, .sb-grid thead .sb-g { background: var(--light-gray); z-index: 4; }
.sb-own { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #024E73; color: #fff; font-weight: 700; font-size: 10px; }
.sb-goal { font-weight: 700; color: var(--primary-color); font-size: 12px; }
.sb-sk { display: inline-flex; align-items: center; gap: 2px; margin-left: 6px; font-size: 11px; font-weight: 700; color: var(--primary-color); }
.sb-sk svg { width: 11px; height: 11px; fill: currentColor; }
td.sb-c { font-weight: 600; font-variant-numeric: tabular-nums; cursor: pointer; }
td.sb-c:hover { outline: 2px solid var(--font-mid-gray); outline-offset: -2px; }
td.sb-c.none { color: var(--font-mid-gray); }
td.sb-c.pass { background: #E3F4E8; color: #2E7D46; }
td.sb-c.fail { background: #FCEBEC; color: var(--primary-color); }
td.sb-c.now { outline: 2px solid var(--dark-gray); outline-offset: -2px; }
td.sb-c.gchg { box-shadow: inset 3px 0 0 var(--dark-gray); }
.sb-cellin { width: 70px; padding: 5px 4px !important; margin: 0 !important; border: 2px solid var(--primary-color) !important; border-radius: 6px; text-align: center; font-size: 12.5px !important; }
.sb-enter { display: inline-block; font-size: 10.5px; font-weight: 700; color: var(--primary-color); border: 1px dashed var(--primary-color); border-radius: 6px; padding: 1px 6px; }
.sb-avg { font-weight: 800; border-left: 2px solid var(--mid-gray); }
.sb-legend { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; font-size: 12px; color: var(--font-dark-gray); margin-top: 10px; }
.sb-legend i { display: inline-block; width: 12px; height: 12px; border-radius: 3px; margin-right: 5px; vertical-align: -2px; border: 1px solid var(--mid-gray); background: #fff; }
.sb-legend i.pass { background: #E3F4E8; }
.sb-legend i.fail { background: #FCEBEC; }
.sb-legend i.gchg { box-shadow: inset 3px 0 0 var(--dark-gray); }

/* Due date modal */
.sb-modal-title { margin: 0 0 6px; font-size: 20px; }
.sb-label { margin: 18px 0 8px; font-size: 14px; font-weight: bold; }
.sb-due-row { display: flex; gap: 15px; }
.sb-due-row > div { flex: 1; min-width: 0; }
.sb-due-row select, .sb-due-row input { width: 100%; margin: 0; }
.sb-modal-acts { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
.sb-modal-acts .btn, .sb-modal-acts .btn-light { margin: 0; padding: 8px 18px; }

@media(max-width: 767px) {
    .sb-cards { grid-template-columns: 1fr; }
    .sb-week { gap: 14px; }
    .sb-tagline { display: none; }
}
</style>
